import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, Package, Truck, Repeat } from 'lucide-react';
import { FaWhatsapp, FaInstagram, FaFacebookF } from 'react-icons/fa';

const CHANNEL_META = {
  whatsapp: { label: 'WhatsApp', icon: <FaWhatsapp className="w-3.5 h-3.5" />, tone: 'text-emerald-600 dark:text-emerald-400' },
  instagram: { label: 'Instagram', icon: <FaInstagram className="w-3.5 h-3.5" />, tone: 'text-pink-600 dark:text-pink-400' },
  messenger: { label: 'Messenger', icon: <FaFacebookF className="w-3.5 h-3.5" />, tone: 'text-blue-600 dark:text-blue-400' },
};

const SCENARIOS = [
  {
    id: 'produk',
    tab: 'Tanya produk & stok',
    icon: <Package className="w-4 h-4" />,
    channel: 'whatsapp',
    poweredBy: ['Katalog Produk', 'AI Agent'],
    caption:
      'AI menjawab dari katalog yang Anda upload — harga, stok, dan varian mengikuti data Anda, bukan karangan.',
    messages: [
      { from: 'customer', text: 'Kak, Hoodie Premium masih ada?' },
      {
        from: 'ai',
        text: 'Ada kak! Hoodie Premium ready stock\n\nStok: 12 pcs\nHarga: Rp 199.000\nSize: M, L, XL\n\nMau ambil size apa kak?',
      },
      { from: 'customer', text: 'Yang L. Bahannya tebal ga?' },
      {
        from: 'ai',
        text: 'Size L tersedia kak. Bahannya fleece 320gsm, tebal tapi tetap adem dipakai harian. Mau sekalian aku buatkan pesanannya?',
      },
    ],
  },
  {
    id: 'ongkir',
    tab: 'Tanya FAQ & ongkir',
    icon: <Truck className="w-4 h-4" />,
    channel: 'whatsapp',
    poweredBy: ['FAQ & SOP', 'Cek Ongkir Kurir'],
    caption:
      'Ongkir diambil dari API kurir untuk kecamatan tujuan, termasuk kota kecil. Pertanyaan kebijakan seperti retur dijawab dari FAQ yang Anda isi sendiri.',
    messages: [
      { from: 'customer', text: 'Ongkir ke Kabanjahe berapa ya kak?' },
      {
        from: 'ai',
        text: 'Kirim ke Kabanjahe, Kab. Karo bisa kak\n\nJNE REG — Rp 32.000 (2-3 hari)\nJ&T EZ — Rp 30.000 (2-3 hari)\n\nMau pakai kurir yang mana?',
      },
      { from: 'customer', text: 'Kalau barangnya tidak sesuai gimana?' },
      {
        from: 'ai',
        text: 'Bisa retur maksimal 3 hari setelah paket diterima kak, dengan video unboxing sebagai bukti. Kalau memang salah kirim dari kami, ongkir returnya kami yang tanggung.',
      },
    ],
  },
  {
    id: 'lintas-kanal',
    tab: 'Lanjut order lintas kanal',
    icon: <Repeat className="w-4 h-4" />,
    channel: 'instagram',
    poweredBy: ['Omnichannel Inbox', 'Pesanan & Pembayaran'],
    caption:
      'Riwayat tiap pelanggan tersimpan per kontak, jadi percakapan bisa dilanjutkan walau pindah kanal. Pesanan dan tagihan dibuat dari chat yang sama.',
    messages: [
      { from: 'customer', text: 'Aku yang DM semalam soal hoodie, jadi ambil 2 ya' },
      {
        from: 'ai',
        text: 'Siap kak, aku lanjut dari obrolan semalam\n\n2x Hoodie Premium (L) — Rp 398.000\n\nKirim ke alamat yang sama seperti pesanan sebelumnya?',
      },
      { from: 'customer', text: 'Iya sama' },
      {
        from: 'ai',
        text: 'Noted kak. Pesanan #NRV-0241 sudah aku buat, tinggal pembayaran\n\nTotal: Rp 428.000 (termasuk ongkir)\n\nLink pembayaran aku kirim di chat ini ya.',
      },
    ],
  },
];

const Bubble = ({ message, index, channel }) => {
  const isAi = message.from === 'ai';
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.5, ease: [0.22, 1, 0.36, 1] }}
      className={`flex gap-2.5 ${isAi ? '' : 'flex-row-reverse'}`}
    >
      <div
        className={`mt-0.5 grid h-7 w-7 flex-shrink-0 place-items-center rounded-full text-xs font-semibold ${
          isAi
            ? 'bg-indigo-600 text-white'
            : 'bg-slate-200 text-slate-600 dark:bg-zinc-700 dark:text-zinc-200'
        }`}
      >
        {isAi ? <Bot className="h-3.5 w-3.5" /> : <span className={CHANNEL_META[channel].tone}>{CHANNEL_META[channel].icon}</span>}
      </div>
      <div
        className={`max-w-[82%] whitespace-pre-line break-words rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed sm:text-sm ${
          isAi
            ? 'rounded-tl-sm bg-slate-100 text-slate-700 dark:bg-zinc-800 dark:text-zinc-200'
            : 'rounded-tr-sm bg-indigo-600 text-white'
        }`}
      >
        {message.text}
      </div>
    </motion.div>
  );
};

/** Simulasi tampilan produk — alur yang benar-benar berjalan di Nerava, bukan fitur karangan. */
export const DemoScenarios = ({ className = '' }) => {
  const [active, setActive] = useState(0);
  const scenario = SCENARIOS[active];

  useEffect(() => {
    const timer = setInterval(() => setActive((prev) => (prev + 1) % SCENARIOS.length), 14000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className={className} data-testid="demo-scenarios">
      <div className="grid gap-8 lg:grid-cols-12 lg:gap-12">
        {/* Pemilih skenario */}
        <div className="lg:col-span-5">
          <div className="flex flex-col gap-2.5" role="tablist" aria-label="Skenario demo Nerava">
            {SCENARIOS.map((item, index) => {
              const isActive = index === active;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  data-testid={`demo-tab-${item.id}`}
                  onClick={() => setActive(index)}
                  className={`group flex w-full items-start gap-3 rounded-2xl border px-4 py-3.5 text-left transition-[border-color,background-color,transform] duration-300 ${
                    isActive
                      ? 'border-indigo-400 bg-indigo-50/70 dark:border-indigo-500/70 dark:bg-indigo-500/10'
                      : 'border-slate-200 bg-white hover:-translate-y-0.5 hover:border-indigo-300 dark:border-zinc-800 dark:bg-zinc-900 dark:hover:border-indigo-500/50'
                  }`}
                >
                  <span
                    className={`mt-0.5 grid h-8 w-8 flex-shrink-0 place-items-center rounded-lg transition-colors ${
                      isActive
                        ? 'bg-indigo-600 text-white'
                        : 'bg-slate-100 text-slate-500 group-hover:text-indigo-600 dark:bg-zinc-800 dark:text-zinc-400'
                    }`}
                  >
                    {item.icon}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-slate-900 dark:text-white">
                      {item.tab}
                    </span>
                    <span className="mt-1 flex flex-wrap gap-1.5">
                      {item.poweredBy.map((chip) => (
                        <span
                          key={chip}
                          className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500 dark:bg-zinc-800 dark:text-zinc-400"
                        >
                          {chip}
                        </span>
                      ))}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          <p className="mt-5 border-l-2 border-indigo-500/40 pl-4 text-sm text-slate-500 dark:text-zinc-400">
            Semua alur di atas memakai fitur yang sudah berjalan: katalog produk, FAQ, cek ongkir kurir,
            pesanan, dan pembayaran.
          </p>
        </div>

        {/* Jendela chat */}
        <div className="lg:col-span-7">
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_30px_70px_-45px_rgba(15,23,42,0.5)] dark:border-zinc-800 dark:bg-zinc-900 dark:shadow-[0_30px_70px_-34px_rgba(0,0,0,0.95)]">
            <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 dark:border-zinc-800">
              <div className="flex min-w-0 items-center gap-2.5">
                <span className={`flex-shrink-0 ${CHANNEL_META[scenario.channel].tone}`}>
                  {CHANNEL_META[scenario.channel].icon}
                </span>
                <span className="truncate text-sm font-semibold text-slate-900 dark:text-white">
                  {CHANNEL_META[scenario.channel].label}
                </span>
                <span className="hidden truncate text-xs text-slate-400 sm:inline dark:text-zinc-500">
                  · Inbox Nerava
                </span>
              </div>
              <span className="flex flex-shrink-0 items-center gap-1.5 text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                AI aktif
              </span>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={scenario.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="space-y-3 p-4 sm:p-5"
              >
                {scenario.messages.map((message, index) => (
                  <Bubble key={`${scenario.id}-${index}`} message={message} index={index} channel={scenario.channel} />
                ))}
              </motion.div>
            </AnimatePresence>

            <div className="border-t border-slate-100 bg-slate-50/70 px-4 py-3 dark:border-zinc-800 dark:bg-zinc-950/40">
              <p className="text-xs leading-relaxed text-slate-500 dark:text-zinc-400">
                {scenario.caption}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoScenarios;
